import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Database,
  FileStack,
  Layers,
  Table2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useApi } from "@/api";
import {
  HierarchyBrowser,
  InlineStat,
  Pagination,
} from "@/components/manage/HierarchyBrowser";
import { PageHeader } from "@/components/manage/PageHeader";
import { DemoUnavailableNote } from "@/components/manage/Section";
import { useIsDemoManage } from "@/components/manage/org-context";

export const Route = createFileRoute("/manage/datasets/$id/data")({
  component: DatasetDataPage,
});

interface DatasetRead {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  description: string | null;
}

interface TableColumn {
  name: string;
  type: string;
}

interface TableEntry {
  name: string;
  columns: TableColumn[];
  row_count: number;
  source_file_id: string | null;
  source_filename: string | null;
}

interface WorkspaceRef {
  id: string;
  name: string;
}

interface TablesResponse {
  dataset_id: string;
  workspace_id: string | null;
  workspace_name: string | null;
  assigned_workspaces: WorkspaceRef[];
  tables: TableEntry[];
}

interface SampleResponse {
  workspace_id: string;
  table: string;
  columns: string[];
  rows: unknown[][];
  row_count: number;
  total_rows: number;
  offset: number;
  limit: number;
}

// Workspace lens, persisted per dataset and shared across the dataset's admin
// views. A dataset is processed uniquely *within* each workspace, so browsing
// its data means choosing which workspace's processed copy you're looking at.
function lensKey(datasetId: string): string {
  return `chai_admin_ds_ws:${datasetId}`;
}

function useDatasetLens(datasetId: string): {
  wsId: string | null;
  setWsId: (id: string | null) => void;
} {
  const [wsId, setState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(lensKey(datasetId));
    } catch {
      return null;
    }
  });
  const setWsId = (id: string | null) => {
    setState(id);
    try {
      if (id) localStorage.setItem(lensKey(datasetId), id);
      else localStorage.removeItem(lensKey(datasetId));
    } catch {
      /* ignore */
    }
  };
  return { wsId, setWsId };
}

interface TableGroup {
  key: string;
  label: string;
  tables: TableEntry[];
}

// mojo names a table's source_filename "<human file> :: <in-file locator>"
// (e.g. "Deck.pdf :: p13 table_4.table") for extracted tables, or a plain
// filename for a whole spreadsheet/csv. Split on the FIRST " :: " so a locator
// containing "::" can't over-split.
function tableFileLabel(t: TableEntry): string {
  if (!t.source_filename) return t.source_file_id ?? t.name;
  const sep = t.source_filename.indexOf(" :: ");
  return (sep >= 0 ? t.source_filename.slice(0, sep) : t.source_filename).trim();
}

function tableLocatorLabel(t: TableEntry): string | null {
  if (!t.source_filename) return null;
  const sep = t.source_filename.indexOf(" :: ");
  if (sep < 0) return null;
  return t.source_filename
    .slice(sep + 4)
    .trim()
    .replace(/\.(table|figure)$/i, "");
}

// Group tables under their source file (preserving catalog order). Keyed on the
// stable source_file_id so two files sharing a display name never merge.
function groupTablesBySource(tables: TableEntry[]): TableGroup[] {
  const order: string[] = [];
  const map = new Map<string, TableGroup>();
  for (const t of tables) {
    const label = tableFileLabel(t);
    // Key on the stable source_file_id; when it's null fall back to the unique
    // table name (NOT the display label) so two distinct same-named files can't
    // merge into one group or collide React keys.
    const key = t.source_file_id ?? `name:${t.name}`;
    let g = map.get(key);
    if (!g) {
      g = { key, label, tables: [] };
      map.set(key, g);
      order.push(key);
    }
    g.tables.push(t);
  }
  return order.map((k) => map.get(k)!);
}

function DatasetDataPage() {
  // The DuckDB browser rides the tables + sample endpoints, which are
  // demo-403'd — render a plain note instead of a broken browser.
  if (useIsDemoManage()) return <DemoUnavailableNote />;
  return <DatasetDataPageInner />;
}

function DatasetDataPageInner() {
  const api = useApi();
  const { id: datasetId } = useParams({ from: "/manage/datasets/$id/data" });
  const [activeTable, setActiveTable] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<number>(50);
  const [pageIndex, setPageIndex] = useState<number>(0);

  const { wsId, setWsId } = useDatasetLens(datasetId);

  const dataset = useQuery({
    queryKey: ["admin-dataset", datasetId],
    queryFn: () => api.request<DatasetRead>(`/admin/datasets/${datasetId}`),
  });

  // Catalog is scoped to the lens. The response also carries
  // `assigned_workspaces` (computed over the whole dataset, lens-independent),
  // so one call powers both the table list and the lens selector.
  const tables = useQuery({
    queryKey: ["admin-dataset-tables", datasetId, wsId ?? ""],
    queryFn: () =>
      api.request<TablesResponse>(
        `/admin/datasets/${datasetId}/tables${wsId ? `?workspace_id=${encodeURIComponent(wsId)}` : ""}`,
      ),
  });

  const assigned = tables.data?.assigned_workspaces ?? [];

  // Default the lens to the first workspace this dataset is processed into.
  // When the stored lens points at a workspace the dataset is no longer in,
  // fall back to the first assigned one so the page never strands on a stale id.
  useEffect(() => {
    if (!assigned.length) return;
    const valid = wsId && assigned.some((w) => w.id === wsId);
    if (!valid) setWsId(assigned[0]!.id);
  }, [assigned, wsId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Select the first table on load, AND re-select when the current table is
  // absent from the catalog (e.g. after switching the workspace lens to a
  // workspace that doesn't have it) so the sample query never strands on a
  // table that doesn't exist in the active workspace.
  useEffect(() => {
    const list = tables.data?.tables;
    if (list?.length && (!activeTable || !list.some((t) => t.name === activeTable))) {
      setActiveTable(list[0]!.name);
    }
  }, [tables.data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageIndex(0);
  }, [activeTable, pageSize, wsId]);

  const offset = pageIndex * pageSize;

  const sample = useQuery({
    queryKey: ["duckdb-sample", wsId, activeTable, offset, pageSize],
    queryFn: () =>
      api.request<SampleResponse>(
        `/admin/workspaces/${wsId}/duckdb/sample?table=${encodeURIComponent(activeTable!)}&limit=${pageSize}&offset=${offset}`,
      ),
    enabled: !!wsId && !!activeTable,
    placeholderData: (prev) => prev,
  });

  const activeMeta = useMemo(
    () => tables.data?.tables.find((t) => t.name === activeTable) ?? null,
    [tables.data, activeTable],
  );

  // Tables grouped by source file for the sidebar hierarchy.
  const tableGroups = useMemo(
    () => groupTablesBySource(tables.data?.tables ?? []),
    [tables.data],
  );
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // Auto-expand the multi-table group holding the active table (additive, so
  // groups the user opened stay open).
  useEffect(() => {
    if (!activeTable || !tableGroups.length) return;
    const active = tableGroups.find((g) =>
      g.tables.some((t) => t.name === activeTable),
    );
    if (active && active.tables.length > 1) {
      setOpenGroups((prev) =>
        prev.has(active.key) ? prev : new Set([...prev, active.key]),
      );
    }
  }, [activeTable, tableGroups]);

  const totalRows = sample.data?.total_rows ?? activeMeta?.row_count ?? 0;
  const lastPage = Math.max(0, Math.ceil(totalRows / pageSize) - 1);
  const startRow = totalRows === 0 ? 0 : offset + 1;
  const endRow = Math.min(offset + pageSize, totalRows);

  // While the lens is resolving to an assigned workspace, keep showing the
  // skeleton rather than flashing the cross-workspace union or an empty state.
  const lensResolving =
    assigned.length > 0 && !assigned.some((w) => w.id === wsId);

  if (dataset.isLoading || tables.isLoading || lensResolving) {
    return (
      <div className="mx-auto max-w-[1200px] px-10 py-10">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="mt-8 h-72 w-full" />
      </div>
    );
  }

  const ds = dataset.data;
  const catalog = tables.data;

  if (!ds) return null;

  // Not assigned anywhere, or assigned but no tabular data yet.
  if (assigned.length === 0 || !catalog || catalog.tables.length === 0) {
    return (
      <div className="mx-auto max-w-[1200px] px-10 py-10">
        <Link
          to="/manage/datasets/$id"
          params={{ id: datasetId }}
          className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3" /> {ds.name}
        </Link>

        <PageHeader
          eyebrow="Dataset · Data"
          tabTitle={["Data", dataset.data?.name ?? "Dataset"]}
          title={
            <span className="inline-flex items-center gap-3">
              <Database className="size-7 text-muted-foreground" />
              Browse data
            </span>
          }
          description={
            assigned.length === 0
              ? "This dataset isn't assigned to any workspace yet — DuckDB tables only exist after assignment."
              : `No tabular data in "${catalog?.workspace_name ?? "this workspace"}" yet — upload CSV or XLSX files to populate it.`
          }
          actions={
            assigned.length > 1 ? (
              <WorkspaceLens
                workspaces={assigned}
                value={wsId}
                onChange={setWsId}
              />
            ) : undefined
          }
        />

        {assigned.length === 0 ? (
          <EmptyAssignmentPanel datasetId={datasetId} />
        ) : (
          <EmptyTablesPanel />
        )}
      </div>
    );
  }

  // Active-table info for the bar above the data table.
  const colTypes = activeMeta
    ? Object.entries(
        activeMeta.columns.reduce<Record<string, number>>((acc, c) => {
          acc[c.type] = (acc[c.type] ?? 0) + 1;
          return acc;
        }, {}),
      )
        .sort((a, b) => b[1] - a[1])
        .map(([t, n]) => `${n} ${t}`)
        .join(" · ")
    : "";

  return (
    <HierarchyBrowser
      crumb={
        <Link
          to="/manage/datasets/$id"
          params={{ id: datasetId }}
          className="mb-4 inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3" /> {ds.name}
        </Link>
      }
      header={{
        eyebrow: "Dataset · Data",
        title: (
          <span className="inline-flex items-center gap-3">
            <Database className="size-7 text-muted-foreground" />
            Browse data
          </span>
        ),
        description: `DuckDB tables from this dataset, sampled from "${catalog.workspace_name}". The data here is what the agent's MCP tools query.`,
        actions:
          assigned.length > 1 ? (
            <WorkspaceLens workspaces={assigned} value={wsId} onChange={setWsId} />
          ) : undefined,
      }}
      stats={[
        {
          label: "Tables",
          value: catalog.tables.length,
          hint: "from this dataset",
        },
        {
          label: "Workspace",
          value: (
            <span className="font-mono text-base">
              {catalog.workspace_name ?? "—"}
            </span>
          ),
          hint: "data source",
        },
        {
          label: "Active",
          value: activeMeta?.row_count.toLocaleString() ?? "—",
          hint: "rows in active table",
        },
        {
          label: "Showing",
          value: `${startRow}–${endRow}`,
          hint: `of ${totalRows.toLocaleString()}`,
        },
      ]}
      sidebarLabel="Source files"
      sidebar={tableGroups.map((g) => {
        const hasActive = g.tables.some((t) => t.name === activeTable);
        // Single-table source = a direct selectable row (no accordion); this is
        // the bare csv/xlsx case and keeps the rail flat when nothing nests.
        if (g.tables.length === 1) {
          const t = g.tables[0]!;
          const isActive = t.name === activeTable;
          return (
            <li key={g.key}>
              <button
                type="button"
                title={t.name}
                aria-current={isActive ? "true" : undefined}
                onClick={() => setActiveTable(t.name)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition",
                  isActive
                    ? "bg-[var(--color-card-tint-lavender)] text-[var(--color-brand-purple-800)]"
                    : "hover:bg-foreground/5",
                )}
              >
                <span
                  aria-hidden
                  className="h-3.5 w-[3px] shrink-0 rounded-full"
                  style={{ backgroundColor: "#b8c2cc" }}
                />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-[13px] font-medium">
                    {g.label}
                  </span>
                  <span className="truncate font-mono text-[10px] text-muted-foreground">
                    {t.columns.length} cols
                  </span>
                </span>
                <span
                  className={cn(
                    "shrink-0 font-mono text-[10px] tabular-nums",
                    isActive
                      ? "text-[var(--color-brand-purple-800)]/70"
                      : "text-muted-foreground",
                  )}
                >
                  {t.row_count.toLocaleString()}
                </span>
              </button>
            </li>
          );
        }
        const open = openGroups.has(g.key) || hasActive;
        return (
          <li key={g.key}>
            <button
              type="button"
              onClick={() => toggleGroup(g.key)}
              aria-expanded={open}
              className="flex w-full items-center gap-1.5 rounded-md px-2.5 py-2 text-left transition hover:bg-foreground/5"
            >
              {open ? (
                <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-3 shrink-0 text-muted-foreground" />
              )}
              <FileStack className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate text-[13px] font-medium text-[var(--color-charcoal)]">
                {g.label}
              </span>
              <span
                className={cn(
                  "shrink-0 font-mono text-[10px] tabular-nums",
                  hasActive
                    ? "text-[var(--color-brand-purple-800)]/70"
                    : "text-muted-foreground",
                )}
              >
                {g.tables.length}
              </span>
            </button>
            {open &&
              g.tables.map((t) => {
                const isActive = t.name === activeTable;
                const loc = tableLocatorLabel(t);
                return (
                  <button
                    key={t.name}
                    type="button"
                    title={t.name}
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => setActiveTable(t.name)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md py-1.5 pl-7 pr-2.5 text-left transition",
                      isActive
                        ? "bg-[var(--color-card-tint-lavender)] text-[var(--color-brand-purple-800)]"
                        : "hover:bg-foreground/5",
                    )}
                  >
                    <Table2 className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-sm font-medium">
                        {loc ?? `${t.row_count.toLocaleString()} rows`}
                      </span>
                      <span className="truncate font-mono text-[10px] text-muted-foreground">
                        {t.columns.length} cols
                      </span>
                    </span>
                    <span
                      className={cn(
                        "shrink-0 font-mono text-[10px] tabular-nums",
                        isActive
                          ? "text-[var(--color-brand-purple-800)]/70"
                          : "text-muted-foreground",
                      )}
                    >
                      {t.row_count.toLocaleString()}
                    </span>
                  </button>
                );
              })}
          </li>
        );
      })}
    >
      {/* Per-table info strip — sits right above the data table. */}
      {activeMeta && (
        <div className="mb-2 flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1.5 rounded-lg border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-4 py-2.5 text-xs">
          <InlineStat
            label="Source"
            value={
              <span>
                {tableFileLabel(activeMeta)}
                {tableLocatorLabel(activeMeta) && (
                  <span className="ml-1 font-mono text-muted-foreground">
                    :: {tableLocatorLabel(activeMeta)}
                  </span>
                )}
              </span>
            }
          />
          <InlineStat
            label="Rows"
            value={
              <span className="font-mono tabular-nums">
                {activeMeta.row_count.toLocaleString()}
              </span>
            }
          />
          <InlineStat
            label="Columns"
            value={
              <span className="font-mono tabular-nums">
                {activeMeta.columns.length}
              </span>
            }
          />
          {colTypes && (
            <InlineStat
              label="Types"
              value={<span className="font-mono">{colTypes}</span>}
            />
          )}
        </div>
      )}

      {/* Scrollable table container — fixed height inside the flex column. */}
      <div className="relative min-h-0 flex-1 overflow-auto rounded-lg border border-[var(--color-hairline)] bg-card">
        {sample.isLoading && !sample.data ? (
          <Skeleton className="h-full w-full rounded-none" />
        ) : sample.error ? (
          <p className="px-4 py-6 text-sm text-[var(--color-semantic-error)]">
            Failed to load: {(sample.error as Error).message}
          </p>
        ) : (
          <table
            className="min-w-full text-sm"
            style={{ borderCollapse: "separate", borderSpacing: 0 }}
          >
            <thead>
              <tr>
                <th
                  className="sticky top-0 left-0 z-30 border-b border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-3 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground"
                  style={{ width: 56 }}
                >
                  #
                </th>
                {sample.data?.columns.map((c) => (
                  <th
                    key={c}
                    className="sticky top-0 z-20 whitespace-nowrap border-b border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-3 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sample.data?.rows.length ? (
                sample.data.rows.map((row, i) => (
                  <tr
                    key={offset + i}
                    className="group border-b border-[var(--color-hairline-soft)] last:border-b-0"
                  >
                    <td
                      className="sticky left-0 z-10 border-b border-[var(--color-hairline-soft)] bg-card px-3 py-2 font-mono text-[10px] tabular-nums text-muted-foreground group-hover:bg-[var(--color-surface)]"
                      style={{ width: 56 }}
                    >
                      {offset + i + 1}
                    </td>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className="max-w-[280px] truncate whitespace-nowrap border-b border-[var(--color-hairline-soft)] px-3 py-2 font-mono text-xs group-hover:bg-[var(--color-surface)]"
                        title={cell === null ? "null" : String(cell)}
                      >
                        {cell === null ? (
                          <span className="text-muted-foreground italic">null</span>
                        ) : (
                          String(cell)
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={(sample.data?.columns.length ?? 0) + 1}
                    className="px-4 py-8 text-center text-xs text-muted-foreground"
                  >
                    No rows on this page.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Pagination
        pageIndex={pageIndex}
        lastPage={lastPage}
        pageSize={pageSize}
        onPageSize={setPageSize}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => Math.min(lastPage, p + 1))}
        onFirst={() => setPageIndex(0)}
        onLast={() => setPageIndex(lastPage)}
        totalRows={totalRows}
        startRow={startRow}
        endRow={endRow}
        fetching={sample.isFetching}
      />
    </HierarchyBrowser>
  );
}

// The workspace lens. A dataset is processed uniquely per workspace, so this
// chooses which workspace's processed copy you're browsing. Only rendered when
// the dataset spans more than one workspace — a single-workspace dataset has no
// choice to make and auto-selects.
function WorkspaceLens({
  workspaces,
  value,
  onChange,
}: {
  workspaces: WorkspaceRef[];
  value: string | null;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        Viewing
      </span>
      <Select
        value={value ?? undefined}
        onValueChange={(v) => {
          if (v) onChange(v);
        }}
      >
        <SelectTrigger className="h-9 w-[210px] border-[var(--color-hairline)] bg-card text-sm">
          <span className="flex min-w-0 items-center gap-1.5">
            <Layers className="size-3.5 shrink-0 text-muted-foreground" />
            <SelectValue placeholder="Workspace…" />
          </span>
        </SelectTrigger>
        <SelectContent>
          {workspaces.map((w) => (
            <SelectItem key={w.id} value={w.id}>
              {w.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function EmptyAssignmentPanel({ datasetId }: { datasetId: string }) {
  return (
    <div className="rounded-lg border border-dashed border-[var(--color-hairline-strong)] bg-card p-10 text-center">
      <Database className="mx-auto size-6 text-muted-foreground" />
      <h2
        className="mt-3 text-base font-semibold tracking-tight"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Not assigned to any workspace yet
      </h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        DuckDB tables are created per-workspace at assignment time. Assign this dataset to a
        workspace to make the data browsable here.
      </p>
      <Button
        render={
          <Link to="/manage/datasets/$id" params={{ id: datasetId }} />
        }
        className="mt-4"
        variant="outline"
      >
        Back to dataset
      </Button>
    </div>
  );
}

function EmptyTablesPanel() {
  return (
    <div className="rounded-lg border border-dashed border-[var(--color-hairline-strong)] bg-card p-10 text-center">
      <Database className="mx-auto size-6 text-muted-foreground" />
      <h2
        className="mt-3 text-base font-semibold tracking-tight"
        style={{ fontFamily: "var(--font-display)" }}
      >
        No tables yet
      </h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        Upload tabular files (CSV or XLSX) to this dataset — they'll appear here after the
        ingestion worker syncs them into the workspace's DuckDB.
      </p>
    </div>
  );
}
